<template>
  <Layout>
    <Head title="Dashboard - Kiosk" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="space-y-6">
        <div class="border-b pb-4">
          <h1 class="text-2xl font-bold text-gray-900">{{ t('dashboard.title') }}</h1>
          <p class="text-gray-600 mt-1">{{ t('ui.welcome') }}! Here's an overview of your account.</p>
        </div>

        <!-- Welcome Section -->
        <Card>
          <CardHeader>
            <CardTitle>{{ t('dashboard.welcome_message', { name: firstName }) }}</CardTitle>
            <CardDescription>
              {{ t('dashboard.good_to_see') }}
            </CardDescription>
          </CardHeader>
        </Card>

        <!-- Recent Activity or Info Section -->
        <template v-if="pendingRequestsCount > 0">
          <Card>
            <CardHeader>
              <CardTitle>{{ t('dashboard.recent_activity') }}</CardTitle>
              <CardDescription>
                {{ t('dashboard.pending_attention') }}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div class="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-blue-100 rounded-lg">
                    <svg class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <p class="font-medium text-blue-900">
                      {{ t('dashboard.request_requires', { count: pendingRequestsCount }) }}
                    </p>
                    <p class="text-sm text-blue-700">{{ t('dashboard.review_respond') }}</p>
                  </div>
                </div>
                <Link
                  href="/invites"
                  class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  {{ t('dashboard.view_requests') }}
                </Link>
              </div>
            </CardContent>
          </Card>
        </template>

        <!-- Quick Stats -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Pending Requests -->
          <Card>
            <CardContent class="p-6">
              <div class="flex items-center space-x-4">
                <div class="p-2 bg-blue-100 rounded-lg">
                  <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p class="text-2xl font-bold text-gray-900">{{ pendingRequestsCount }}</p>
                  <p class="text-sm text-gray-600">{{ t('dashboard.pending_request', { count: pendingRequestsCount }) }}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <!-- Appointments -->
          <Card>
            <CardContent class="p-6">
              <div class="flex items-center space-x-4">
                <div class="p-2 bg-green-100 rounded-lg">
                  <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p class="text-2xl font-bold text-gray-900">{{ appointmentsCount || 0 }}</p>
                  <p class="text-sm text-gray-600">{{ t('dashboard.scheduled_appointment', { count: appointmentsCount || 0 }) }}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <!-- Profile Status -->
          <Card>
            <CardContent class="p-6">
              <div class="flex items-center space-x-4">
                <div class="p-2 bg-purple-100 rounded-lg">
                  <svg class="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p class="text-2xl font-bold text-gray-900">✓</p>
                  <p class="text-sm text-gray-600">{{ t('dashboard.profile_complete') }}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- Quick Actions -->
        <Card>
          <CardHeader>
            <CardTitle>{{ t('dashboard.quick_actions') }}</CardTitle>
            <CardDescription>
              {{ t('dashboard.common_tasks') }}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                href="/invites"
                class="flex items-center p-4 border rounded-lg hover:shadow-md transition-shadow group"
              >
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                    <svg class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ t('dashboard.view_requests') }}</p>
                    <p class="text-sm text-gray-600">{{ t('dashboard.review_pending') }}</p>
                  </div>
                </div>
              </Link>

              <Link
                href="/appointments"
                class="flex items-center p-4 border rounded-lg hover:shadow-md transition-shadow group"
              >
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-green-50 rounded-lg group-hover:bg-green-100 transition-colors">
                    <svg class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ t('dashboard.view_appointments') }}</p>
                    <p class="text-sm text-gray-600">{{ t('dashboard.check_scheduled') }}</p>
                  </div>
                </div>
              </Link>

              <Link
                href="/profile"
                class="flex items-center p-4 border rounded-lg hover:shadow-md transition-shadow group"
              >
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-purple-50 rounded-lg group-hover:bg-purple-100 transition-colors">
                    <svg class="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ t('dashboard.update_profile') }}</p>
                    <p class="text-sm text-gray-600">{{ t('dashboard.manage_info') }}</p>
                  </div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </Layout>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import Layout from '@/Layouts/Minimal.vue'
import { t } from '../entrypoints/translations.js'

defineProps({
  firstName: String,
  pendingRequestsCount: Number,
  appointmentsCount: Number,
})
</script>
